import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, Stop, Plus, CalendarPlus, ArrowsClockwise, Trash, PencilSimple, WarningCircle } from "@phosphor-icons/react";

const WD = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const toISO = (local) => local ? new Date(local).toISOString() : "";

export function AdminSchedule({ onRequestsChanged }) {
  const [employees, setEmployees] = useState([]);
  const [sel, setSel] = useState("");
  const [sched, setSched] = useState({ shifts: [], recurring: [] });
  const [requests, setRequests] = useState([]);
  const [shift, setShift] = useState({ date: "", start: "09:00", end: "17:00", note: "" });
  const [rec, setRec] = useState({ weekday: 5, start: "09:00", end: "17:00", note: "" });
  const [past, setPast] = useState({ date: "", start: "09:00", end: "17:00" });
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "" });

  const loadEmps = useCallback(async () => {
    const r = await api.get("/employees");
    setEmployees(r.data);
    if (r.data[0] && !sel) setSel(r.data[0].id);
  }, [sel]);
  const loadReq = useCallback(async () => setRequests((await api.get("/admin/schedule-requests")).data), []);
  const loadSched = useCallback(async (id) => { if (id) setSched((await api.get(`/schedules?user_id=${id}`)).data); }, []);
  const loadEntries = useCallback(async (id) => { if (id) setEntries((await api.get(`/time-entries?user_id=${id}`)).data); }, []);

  useEffect(() => { loadEmps(); loadReq(); }, [loadEmps, loadReq]);
  useEffect(() => { loadSched(sel); loadEntries(sel); }, [sel, loadSched, loadEntries]);

  const clock = async (dir) => {
    try { await api.post(`/admin/clock/${dir}`, { employee_id: sel }); toast.success(`Clocked ${dir}`); loadEntries(sel); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addPast = async () => {
    try {
      await api.post("/admin/time-entries", { employee_id: sel, clock_in: `${past.date}T${past.start}:00`, clock_out: `${past.date}T${past.end}:00` });
      toast.success("Shift added"); setPast({ ...past, date: "" }); loadEntries(sel);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openEdit = (e) => {
    setEditing(e);
    setEditForm({ clock_in: toLocalInput(e.clock_in), clock_out: toLocalInput(e.clock_out) });
  };
  const saveEdit = async () => {
    if (!editForm.clock_in) { toast.error("Clock-in time is required"); return; }
    try {
      await api.put(`/admin/time-entries/${editing.id}`, {
        clock_in: toISO(editForm.clock_in),
        clock_out: editForm.clock_out ? toISO(editForm.clock_out) : "",
      });
      toast.success("Time entry updated"); setEditing(null); loadEntries(sel);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delEntry = async (id) => {
    if (!window.confirm("Remove this time entry?")) return;
    try { await api.delete(`/admin/time-entries/${id}`); toast.success("Entry removed"); loadEntries(sel); }
    catch { toast.error("Failed"); }
  };
  const addShift = async () => {
    if (!shift.date) return toast.error("Pick a date");
    try { await api.post("/admin/schedules", { employee_id: sel, ...shift }); toast.success("Shift scheduled"); setShift({ ...shift, note: "" }); loadSched(sel); }
    catch { toast.error("Failed"); }
  };
  const addRec = async () => {
    try { await api.post("/admin/schedules/recurring", { employee_id: sel, ...rec }); toast.success("Recurring shift added"); loadSched(sel); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/schedules/${id}`); loadSched(sel); };

  return (
    <div className="space-y-6">
      <div>
        <Label className="label-caps">Contractor</Label>
        <select className="mt-2 w-full sm:w-80 h-11 border-2 border-input bg-background px-3 rounded-lg font-medium" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="admin-sched-select">
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.title ? ` · ${e.title}` : ""}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-2 p-5">
          <div className="label-caps mb-3">Manual clock (now)</div>
          <div className="flex gap-3">
            <Button className="rounded-lg flex-1" onClick={() => clock("in")} data-testid="admin-clockin"><Play size={16} weight="fill" className="mr-1" /> In</Button>
            <Button variant="destructive" className="rounded-lg flex-1" onClick={() => clock("out")} data-testid="admin-clockout"><Stop size={16} weight="fill" className="mr-1" /> Out</Button>
          </div>
        </Card>
        <Card className="rounded-2xl border-2 p-5">
          <div className="label-caps mb-3">Add past shift</div>
          <Input type="date" className="rounded-lg mb-2" value={past.date} onChange={(e) => setPast({ ...past, date: e.target.value })} data-testid="admin-past-date" />
          <div className="flex gap-2 mb-2">
            <Input type="time" className="rounded-lg" value={past.start} onChange={(e) => setPast({ ...past, start: e.target.value })} data-testid="admin-past-start" />
            <Input type="time" className="rounded-lg" value={past.end} onChange={(e) => setPast({ ...past, end: e.target.value })} data-testid="admin-past-end" />
          </div>
          <Button className="rounded-lg w-full" onClick={addPast} data-testid="admin-past-save"><Plus size={16} className="mr-1" /> Add hours</Button>
        </Card>
        <Card className="rounded-2xl border-2 p-5">
          <div className="label-caps mb-3">Recurring weekly shift</div>
          <select className="w-full h-10 border-2 border-input bg-background px-3 rounded-lg mb-2" value={rec.weekday} onChange={(e) => setRec({ ...rec, weekday: Number(e.target.value) })} data-testid="admin-rec-weekday">
            {WD.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="flex gap-2 mb-2">
            <Input type="time" className="rounded-lg" value={rec.start} onChange={(e) => setRec({ ...rec, start: e.target.value })} data-testid="admin-rec-start" />
            <Input type="time" className="rounded-lg" value={rec.end} onChange={(e) => setRec({ ...rec, end: e.target.value })} data-testid="admin-rec-end" />
          </div>
          <Button className="rounded-lg w-full" onClick={addRec} data-testid="admin-rec-save"><ArrowsClockwise size={16} className="mr-1" /> Add weekly</Button>
        </Card>
      </div>

      <Card className="rounded-2xl border-2 p-5">
        <div className="label-caps mb-3">Timesheet — correct hours & fix missed punches</div>
        <div className="space-y-1.5 font-mono text-sm max-h-96 overflow-y-auto" data-testid="admin-timesheet-list">
          {entries.length === 0 && <div className="text-muted-foreground">No time entries for this contractor yet.</div>}
          {entries.map((e) => {
            const missing = !e.clock_out;
            return (
              <div key={e.id} className={`flex items-center justify-between gap-3 border-b border-border py-2 ${missing ? "bg-amber-50" : ""}`} data-testid={`timesheet-row-${e.id}`}>
                <div className="min-w-0">
                  <div className="truncate">
                    {fmtDT(e.clock_in)} <span className="text-muted-foreground">→</span> {missing
                      ? <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`missing-punch-${e.id}`}><WarningCircle size={13} weight="fill" /> Missing clock-out</span>
                      : fmtDT(e.clock_out)}
                  </div>
                  <div className="text-[0.7rem] text-muted-foreground">
                    {missing ? "In progress" : `${(e.hours ?? 0).toFixed(2)} hrs`}
                    {e.manual ? " · manual" : ""}{e.edited ? " · edited" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant={missing ? "default" : "outline"} className="rounded-lg h-8" onClick={() => openEdit(e)} data-testid={`edit-entry-${e.id}`}>
                    <PencilSimple size={13} className="mr-1" /> {missing ? "Fix punch" : "Edit"}
                  </Button>
                  <button onClick={() => delEntry(e.id)} className="text-destructive p-2" data-testid={`del-entry-${e.id}`}><Trash size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="rounded-none" data-testid="edit-entry-dialog">
          <DialogHeader><DialogTitle>{editing && !editing.clock_out ? "Fix missed punch" : "Correct time entry"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-caps">Clock in</Label>
              <Input type="datetime-local" className="rounded-none mt-1" value={editForm.clock_in} onChange={(ev) => setEditForm({ ...editForm, clock_in: ev.target.value })} data-testid="edit-clock-in" />
            </div>
            <div>
              <Label className="label-caps">Clock out</Label>
              <Input type="datetime-local" className="rounded-none mt-1" value={editForm.clock_out} onChange={(ev) => setEditForm({ ...editForm, clock_out: ev.target.value })} data-testid="edit-clock-out" />
              <p className="text-[0.7rem] text-muted-foreground mt-1">Leave blank to keep the shift open (still clocked in).</p>
            </div>
            <DialogFooter>
              <Button className="rounded-none w-full" onClick={saveEdit} data-testid="edit-entry-save">Save changes</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-2 p-5">
        <div className="label-caps mb-3">Schedule one-off shift</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <Input type="date" className="rounded-lg" value={shift.date} onChange={(e) => setShift({ ...shift, date: e.target.value })} data-testid="admin-shift-date" />
          <Input type="time" className="rounded-lg" value={shift.start} onChange={(e) => setShift({ ...shift, start: e.target.value })} data-testid="admin-shift-start" />
          <Input type="time" className="rounded-lg" value={shift.end} onChange={(e) => setShift({ ...shift, end: e.target.value })} data-testid="admin-shift-end" />
          <Input className="rounded-lg" placeholder="Note" value={shift.note} onChange={(e) => setShift({ ...shift, note: e.target.value })} data-testid="admin-shift-note" />
        </div>
        <Button className="rounded-lg mt-3" onClick={addShift} data-testid="admin-shift-save"><CalendarPlus size={16} className="mr-1" /> Schedule shift</Button>
      </Card>

      <Card className="rounded-2xl border-2 p-5">
        <div className="label-caps mb-3">Current schedule</div>
        <div className="space-y-2 font-mono text-sm" data-testid="admin-sched-list">
          {sched.recurring.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-border pb-1">
              <span>Every {WD.find(([, v]) => v === r.weekday)?.[0]} · {r.start}–{r.end} {r.note && `· ${r.note}`} <span className="text-[0.6rem] uppercase bg-muted px-1 rounded">weekly</span></span>
              <button onClick={() => del(r.id)} className="text-destructive" data-testid={`del-recurring-${r.id}`}><Trash size={14} /></button>
            </div>
          ))}
          {sched.shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between border-b border-border pb-1">
              <span>{s.date} · {s.start}–{s.end} {s.note && `· ${s.note}`}</span>
              <button onClick={() => del(s.id)} className="text-destructive" data-testid={`del-shift-${s.id}`}><Trash size={14} /></button>
            </div>
          ))}
          {sched.recurring.length === 0 && sched.shifts.length === 0 && <div className="text-muted-foreground">No schedule yet.</div>}
        </div>
      </Card>

      <Card className="rounded-2xl border-2 p-5">
        <div className="label-caps mb-3 flex items-center gap-2">
          Change requests
          {requests.filter((r) => r.status !== "resolved").length > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[11px] font-bold text-white bg-destructive rounded-full" data-testid="open-requests-count">
              {requests.filter((r) => r.status !== "resolved").length} new
            </span>
          )}
        </div>
        <div className="space-y-2 text-sm" data-testid="admin-requests">
          {requests.length === 0 && <div className="text-muted-foreground">No requests.</div>}
          {requests.map((r) => (
            <div key={r.id} className={`flex items-center justify-between border-b border-border pb-2 ${r.status === "resolved" ? "opacity-50" : ""}`}>
              <div><span className="font-semibold">{r.user_name}</span>: {r.message}</div>
              {r.status !== "resolved" && <Button size="sm" variant="outline" className="rounded-lg" onClick={async () => { await api.post(`/admin/schedule-requests/${r.id}/resolve`); await loadReq(); onRequestsChanged?.(); }} data-testid={`resolve-${r.id}`}>Resolve</Button>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
